/**
 * FundraiserActionsDropdown.tsx - Dashboard Fundraiser Actions Menu
 * 
 * This component provides a dropdown menu with management actions for
 * fundraisers in the dashboard table view with portal rendering.
 * 
 * Key Features:
 * - Comprehensive fundraiser action menu
 * - Portal rendering for proper z-index layering
 * - Click-outside detection for dropdown closing
 * - Icon-based actions with clear labeling
 * - Responsive dropdown positioning
 * - Conditional action availability
 * 
 * Available Actions:
 * - View: Navigate to fundraiser details page
 * - Edit: Open fundraiser in builder
 * - Donate: Navigate to fundraiser donation page
 * - Invite: Open promoter invitation interface
 * - Copy: Duplicate fundraiser with customization
 * - Delete: Remove fundraiser with confirmation
 * 
 * Props:
 * - onView: () => void - View details handler
 * - onEdit: () => void - Edit fundraiser handler
 * - onDonate: () => void - Donate action handler
 * - onInvite: () => void - Invite promoters handler
 * - onCopy: () => void - Copy fundraiser handler
 * - onDelete: () => void - Delete fundraiser handler
 * 
 * Dependencies: Styled-components, Lucide icons, portal rendering
 */

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronDown, Eye, Copy, Heart, UserPlus, Edit, Trash2 } from 'lucide-react';
import { createPortal } from 'react-dom';

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButtonContainer = styled.div`
  display: flex;
  align-items: center;
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
  }
`;

const EditMainButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  border-radius: 8px 0 0 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #1e293b;
  }
`;

const DropdownTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: transparent;
  border: none;
  border-left: 1px solid #e2e8f0;
  color: #64748b;
  cursor: pointer;
  border-radius: 0 8px 8px 0;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #1e293b;
  }
`;

const ChevronIcon = styled(ChevronDown)<{ isOpen: boolean }>`
  transition: transform 0.2s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  flex-shrink: 0;
`;

const DropdownMenu = styled.div<{ isOpen: boolean; showAbove: boolean; position: { top: number; right: number } }>`
  position: absolute;
  top: ${props => props.showAbove ? 'auto' : '100%'};
  bottom: ${props => props.showAbove ? '100%' : 'auto'};
  right: 0;
  z-index: 1000;
  min-width: 200px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 
    (props.showAbove ? 'translateY(-4px)' : 'translateY(4px)') : 
    (props.showAbove ? 'translateY(-8px)' : 'translateY(0)')
  };
  transition: all 0.2s ease;
  margin-top: ${props => props.showAbove ? '0' : '4px'};
  margin-bottom: ${props => props.showAbove ? '4px' : '0'};
  pointer-events: auto;
`;

const DropdownItem = styled.button<{ variant?: 'danger' }>`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: #ffffff;
  color: ${props => props.variant === 'danger' ? '#dc2626' : '#1e293b'};
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: ${props => props.variant === 'danger' ? '#fef2f2' : '#f8fafc'};
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }
`;

const ItemIcon = styled.div<{ variant?: 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.variant === 'danger' ? '#dc2626' : '#64748b'};
  flex-shrink: 0;
`;

const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 4px 0;
`;

interface FundraiserActionsDropdownProps {
 isLastThreeRows?: boolean;
  onEdit: () => void;
  onDetails: () => void;
  onDonate: () => void;
  onInvite: () => void;
  onLink: () => void;
  onShare: () => void;
  onEmbed: () => void;
  onCopy: () => void;
  onDelete: () => void;
}

export const FundraiserActionsDropdown: React.FC<FundraiserActionsDropdownProps> = ({
 isLastThreeRows = false,
  onEdit,
  onDetails,
  onDonate,
  onInvite,
  onLink,
  onShare,
  onEmbed,
  onCopy,
  onDelete
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAbove, setShowAbove] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleEditClick = () => {
    onEdit();
  };

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const shouldShowAbove = isLastThreeRows;
    setShowAbove(shouldShowAbove);
    
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (onClick: () => void) => {
    onClick();
    setIsOpen(false);
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButtonContainer>
        <EditMainButton onClick={handleEditClick} type="button">
          <Edit size={16} />
          Edit
        </EditMainButton>
        <DropdownTrigger onClick={handleDropdownToggle} type="button">
          <ChevronIcon size={16} isOpen={isOpen} />
        </DropdownTrigger>
      </DropdownButtonContainer>

      <DropdownMenu 
        isOpen={isOpen} 
        showAbove={showAbove}
        position={dropdownPosition}
      >
        <DropdownItem
          type="button"
          onClick={() => handleOptionClick(onDetails)}
        >
          <ItemIcon>
            <Eye size={16} />
          </ItemIcon>
          Details
        </DropdownItem>
        
        <DropdownItem
          type="button"
          onClick={() => handleOptionClick(onDonate)}
        >
          <ItemIcon>
            <Heart size={16} />
          </ItemIcon>
          Donate
        </DropdownItem>
        
        <DropdownItem
          type="button"
          onClick={() => handleOptionClick(onInvite)}
        >
          <ItemIcon>
            <UserPlus size={16} />
          </ItemIcon>
          Invite
        </DropdownItem>
          
          <DropdownItem
            type="button"
            onClick={() => handleOptionClick(onCopy)}
          >
            <ItemIcon>
              <Copy size={16} />
            </ItemIcon>
            Copy
          </DropdownItem>
          
          <Divider />
          
          <DropdownItem
            variant="danger"
            type="button"
            onClick={() => handleOptionClick(onDelete)}
          >
            <ItemIcon variant="danger">
              <Trash2 size={16} />
            </ItemIcon>
            Delete
          </DropdownItem>
        </DropdownMenu>
    </DropdownContainer>
  );
};